/* Similar Art — picks anchor tags from the current product, fetches candidates
 * tagged with each anchor via /collections/all/<tag>?view=similar-art-ajax,
 * dedupes, scores by tag overlap with the source product, and renders the
 * top N as styled product cards.
 *
 * Source tags arrive via data-source-tags (pipe-joined). The artist name
 * arrives via data-artist-name and is excluded from anchor selection so we
 * don't end up showing only "more from same artist" — that's a separate
 * feature.
 */

// Super-common tags that don't carry similarity signal. They still count in
// the overlap score (small bonus), but we avoid using them as the *fetch
// anchor* because a fetch on "nature" would return half the catalog.
const ANCHOR_STOPLIST = new Set([
  'nature', 'tranquility', 'serene', 'serenity', 'peaceful', 'tranquil',
  'landscape', 'painting', 'photograph', 'digital art', 'wildlife',
  'vibrant', 'colorful', 'whimsical', 'ethereal', 'nostalgia', 'beauty',
  'natural beauty', 'artistic', 'art', 'outdoor', 'outdoors', 'scenic',
  'atmospheric', 'vibrant colors', 'soft colors', 'warm colors', 'cool colors',
  'warm tones', 'cool tones', 'earthy tones', 'pastel colors', 'colors'
])

function normalize(s) {
  return (s || '').trim().toLowerCase()
}

function handleize(s) {
  // Mirror Shopify's handleize for tag URLs: lowercase, non-alnum -> dash.
  return normalize(s).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

// Heuristic: tags that look like a person's full name (e.g. "Brandon Angler",
// "Sarah Corvett") — used to filter out artist-name tags when the
// custom.artist_name metafield is empty. Pixels uploads everything else in
// lowercase, so a strict Title Case pattern is a high-precision signal.
function looksLikePersonName(t) {
  return /^[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+$/.test(t.trim())
}

// Pick up to N anchor tags: non-stoplist, not the artist's name, prefer
// multi-word (more specific). Falls back to single-word non-stoplist tags
// if there aren't enough multi-word candidates.
function pickAnchorTags(tags, artistName, maxAnchors) {
  const artist = normalize(artistName)
  const filtered = (tags || []).map(t => t.trim()).filter(t => {
    const n = normalize(t)
    if (!n || ANCHOR_STOPLIST.has(n)) return false
    if (artist && n === artist) return false
    if (looksLikePersonName(t)) return false
    return true
  })
  const multiWord = filtered.filter(t => /\s/.test(t))
  const pool = multiWord.length >= maxAnchors ? multiWord : filtered
  return pool.slice(0, maxAnchors)
}

class SimilarArt extends HTMLElement {
  async connectedCallback() {
    this.grid = this.querySelector('[data-similar-art-grid]')
    if (!this.grid) return

    const sourceId = this.dataset.productId
    const limit = parseInt(this.dataset.limit, 10) || 5
    const maxAnchors = parseInt(this.dataset.maxAnchors, 10) || 2
    const sourceTags = (this.dataset.sourceTags || '').split('|').map(t => t.trim()).filter(Boolean)
    const sourceTagSet = new Set(sourceTags.map(normalize))
    const artistName = this.dataset.artistName || ''

    const anchors = pickAnchorTags(sourceTags, artistName, maxAnchors)
    if (!anchors.length) { this.classList.add('hide'); return }

    let responses
    try {
      responses = await Promise.all(anchors.map(t =>
        fetch(`/collections/all/${handleize(t)}?view=similar-art-ajax`, { credentials: 'same-origin' })
          .then(r => (r.ok ? r.text() : ''))
          .catch(() => '')
      ))
    } catch (e) {
      this.classList.add('hide'); return
    }

    // Collect unique candidates across all anchor fetches
    const candidates = new Map()
    const parser = document.createElement('div')
    for (const html of responses) {
      if (!html) continue
      parser.innerHTML = html
      parser.querySelectorAll('.similar-art-candidate').forEach(el => {
        const id = el.dataset.productId
        if (!id || id === sourceId || candidates.has(id)) return
        const tags = (el.dataset.tags || '').split('|').map(t => t.trim()).filter(Boolean)
        candidates.set(id, { el, tags })
      })
    }

    if (!candidates.size) { this.classList.add('hide'); return }

    // Score by tag overlap with the source product
    const scored = []
    for (const c of candidates.values()) {
      let overlap = 0
      for (const t of c.tags) if (sourceTagSet.has(normalize(t))) overlap++
      scored.push({ el: c.el, score: overlap })
    }
    scored.sort((a, b) => b.score - a.score)
    const top = scored.slice(0, limit)

    // Replace placeholder grid with the chosen cards (unwrap from .similar-art-candidate)
    this.grid.innerHTML = ''
    for (const s of top) {
      const card = s.el.firstElementChild
      if (card) this.grid.appendChild(card)
    }

    // Show the section (was hidden until populated)
    this.removeAttribute('hidden')
  }
}

customElements.define('similar-art', SimilarArt)
